import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { FaQrcode } from 'react-icons/fa';
import { ChevronDownIcon } from 'lucide-react';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTableColumnHeader } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTableColumnHeader';
import { OverlayPopover } from '../../components/OverlayPopover';
import { getContainerQrCodeImgUrl } from '@/utils/qrCode';
import { ContainerLocationStatus } from '@/utils/containerLocation';

/** One open checkout (Integration/GetTestableCheckouts) a webhook can be simulated for. */
export type TestableCheckout = {
  containerLocationId: number;
  containerId: string;
  containerName: string;
  ownerSince: string;
  returnDeadline: string | null;
  status: ContainerLocationStatus;
  externalUserId: string;
};

export type SimulateWebhookType = 'reminder' | 'return' | 'invoice';

/** Milestone submenu label: the days until the (moved) deadline. */
export function getMilestoneLabel(days: number) {
  return days === 0
    ? i18n.t('Integrations:milestoneDueToday')
    : i18n.t('Integrations:milestoneDueInDays', { count: days });
}

function renderContainerNameCell(
  checkout: TestableCheckout,
  canAccessPartnerData: boolean,
) {
  if (!canAccessPartnerData) {
    return checkout.containerName;
  }

  return (
    <Link to={`/container/${checkout.containerId}`}>
      {checkout.containerName}
      <OverlayPopover
        body={
          <img
            alt="QR code for container"
            src={getContainerQrCodeImgUrl(checkout.containerId)}
          />
        }
        header={i18n.t('MerchantTransactions:QRCodeFor', {
          name: checkout.containerName,
        })}
      >
        <FaQrcode className="ms-1" style={{ cursor: 'pointer' }} />
      </OverlayPopover>
    </Link>
  );
}

/** Only CheckedOut/Overdue rows exist here; anything else falls back to the enum name. */
function renderStatusBadge(status: ContainerLocationStatus) {
  switch (status) {
    case ContainerLocationStatus.CheckedOut:
      return (
        <Badge variant="secondary">
          {i18n.t('MerchantTransactions:holdingStatusActive')}
        </Badge>
      );
    case ContainerLocationStatus.Overdue:
      return (
        <Badge variant="destructive">
          {i18n.t('Integrations:checkoutStatusOverdue')}
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary">{ContainerLocationStatus[status]}</Badge>
      );
  }
}

export type GetSimulateWebhooksColumnsOptions = {
  canAccessPartnerData: boolean;
  /** Opens the confirm flow for one webhook simulation. */
  onSimulate: (
    checkout: TestableCheckout,
    type: SimulateWebhookType,
    daysToReturn?: number,
  ) => void;
  /** The daysToReturn values the reminder webhook accepts (from the backend). */
  reminderDayMilestones: number[];
  /** Location id of the row whose simulation is in flight, if any. */
  simulatingId: number | null;
};

export function getSimulateWebhooksColumns({
  canAccessPartnerData,
  onSimulate,
  reminderDayMilestones,
  simulatingId,
}: GetSimulateWebhooksColumnsOptions): ColumnDef<TestableCheckout>[] {
  return [
    {
      accessorKey: 'containerName',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:containerName')}
        />
      ),
      cell: ({ row }) =>
        renderContainerNameCell(row.original, canAccessPartnerData),
      meta: {
        getName: () => i18n.t('MerchantTransactions:containerName'),
      },
    },
    {
      accessorKey: 'externalUserId',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:externalIntegrationUserId')}
        />
      ),
      cell: ({ row }) => (
        <span className="tw:break-all">{row.original.externalUserId}</span>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:externalIntegrationUserId'),
      },
    },
    {
      accessorKey: 'ownerSince',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:checkoutDate')}
        />
      ),
      cell: ({ row }) => (
        <span title={row.original.ownerSince}>
          {new Date(row.original.ownerSince).toLocaleDateString(
            i18n.language,
            getDateFormatOptions(true),
          )}
        </span>
      ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:checkoutDate'),
      },
    },
    {
      accessorKey: 'returnDeadline',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:returnDeadline')}
        />
      ),
      cell: ({ row }) =>
        row.original.returnDeadline ? (
          <span title={row.original.returnDeadline}>
            {new Date(row.original.returnDeadline).toLocaleDateString(
              i18n.language,
              getDateFormatOptions(true),
            )}
          </span>
        ) : (
          '-'
        ),
      meta: {
        getName: () => i18n.t('MerchantTransactions:returnDeadline'),
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={i18n.t('MerchantTransactions:status')}
        />
      ),
      cell: ({ row }) => renderStatusBadge(row.original.status),
      meta: {
        getName: () => i18n.t('MerchantTransactions:status'),
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      enableHiding: false,
      header: () => <div>{i18n.t('MerchantTransactions:actions')}</div>,
      cell: ({ row }) => {
        const isSimulating = simulatingId === row.original.containerLocationId;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={isSimulating}>
                {isSimulating && <Spinner className="tw:mr-2" />}
                {i18n.t('Integrations:simulateWebhook')}
                <ChevronDownIcon className="tw:size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* The reminder needs a daysToReturn milestone, so it is a submenu of the
                  valid values instead of a free number input. */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  {i18n.t('Integrations:webhookReminder')}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {reminderDayMilestones.map((days) => (
                    <DropdownMenuItem
                      key={days}
                      onClick={() => onSimulate(row.original, 'reminder', days)}
                    >
                      {getMilestoneLabel(days)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuItem
                onClick={() => onSimulate(row.original, 'return')}
              >
                {i18n.t('Integrations:webhookReturn')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onSimulate(row.original, 'invoice')}
              >
                {i18n.t('Integrations:webhookInvoice')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}
